import React, { useEffect, useState } from "react";
import { Paper, useMediaQuery, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

// Fetch real credit history data

const CreditHistory = (props: any) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // Responsive detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const clientCurrency = (props?.config?.currency || "GBP").toUpperCase();

  const formatCurrencyAmount = (amount: number, currency?: string) => {
    const safeCurrency = (currency || clientCurrency).toUpperCase();
    const numericAmount = Number(amount || 0);

    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: safeCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericAmount);
    } catch {
      return `${safeCurrency} ${numericAmount.toFixed(2)}`;
    }
  };

  // Define columns based on screen size
  const columns = isMobile
    ? [
        { field: "date", headerName: "Date", flex: 1, minWidth: 80 },
        { field: "delta", headerName: "+/-", flex: 0.5, minWidth: 50 },
        { field: "type", headerName: "Type", flex: 1, minWidth: 80 },
      ]
    : [
        { field: "date", headerName: "Date", flex: 1, minWidth: 120 },
        { field: "delta", headerName: "+/-", flex: 0.5, minWidth: 60 },
        { field: "amount", headerName: "Amount", flex: 1, minWidth: 100 },
        { field: "type", headerName: "Type", flex: 1, minWidth: 120 },
        { field: "message", headerName: "Message", flex: 2, minWidth: 180 },
      ];

  // Extract token helper functions from props
  const { getConsumerToken, makeConsumerRequest } = props;

  useEffect(() => {
    async function fetchCreditHistory() {
      setLoading(true);
      try {
        // Get consumerId from props.auth or localStorage
        const consumerId =
          props?.auth?.consumerId ||
          props?.auth?.id ||
          localStorage.getItem("consumerId");
        const token =
          getConsumerToken?.() || localStorage.getItem("consumerToken");
        if (!consumerId || !token) return;

        const res = makeConsumerRequest
          ? await makeConsumerRequest(`/api/consumer/${consumerId}/credits/history`)
          : await fetch(`/api/consumer/${consumerId}/credits/history`, {
              headers: { Authorization: `Bearer ${token}` },
            });
        if (res.ok) {
          const data = await res.json();
          // Map API data to DataGrid rows
          const mapped = (data?.data || []).map((item: any, idx: number) => ({
            id: item.id || idx,
            date: item.dateTime ? new Date(item.dateTime).toLocaleDateString() : "",
            delta: item.amount > 0 ? `+${item.amount}` : `${item.amount}`,
            amount: Math.abs(item.amount),
            type: item.type || "",
            message: item.message || item.note || "",
            monetary: item.monetaryAmount
              ? formatCurrencyAmount(item.monetaryAmount, item.currency || clientCurrency)
              : "",
          }));
          setRows(mapped);
        }
      } catch (err) {
        // Optionally handle error
      }
      setLoading(false);
    }
    fetchCreditHistory();
  }, []);

  return (
    <div className="credits-container">
      <Paper
        sx={{
          height: isMobile ? 350 : 400,
          width: "100%",
          borderRadius: 2,
          boxShadow: 2,
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={isMobile ? 32 : 36}
          loading={loading}
          pageSizeOptions={[5, 10]}
          initialState={{
            pagination: { paginationModel: { pageSize: 5, page: 0 } },
          }}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            fontSize: isMobile ? 12 : 14,
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "11px" : "14px",
              padding: isMobile ? "4px 8px" : "8px 16px",
            },
          }}
        />
      </Paper>
    </div>
  );
};

export default CreditHistory;
